import React, { useState, useEffect, useCallback } from 'react';
import { Award, Clock, Users, CheckCircle, Eye, Filter, BarChart } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import api from '../../api';

const submissionBadge = (s) => s === 'graded' ? 'bg-emerald-50 text-emerald-700' : s === 'late' ? 'bg-red-50 text-red-700' : 'bg-blue-50 text-blue-700';

export default function SchoolAssignmentsPage() {
  const [assignments, setAssignments] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [viewAsgn, setViewAsgn] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [page, setPage] = useState(1);

  const fetchAssignments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/assignments?page=${page}&limit=20${filterStatus ? `&status=${filterStatus}` : ''}`);
      setAssignments(res.data || []);
      setTotal(res.total || 0);
    } catch { toast.error('Failed to load assignments'); }
    finally { setLoading(false); }
  }, [page, filterStatus]);

  useEffect(() => { fetchAssignments(); }, [fetchAssignments]);

  const totalSubmissions = assignments.reduce((s, a) => s + (a.submission_count || 0), 0);
  const totalGraded = assignments.reduce((s, a) => s + (a.graded_count || 0), 0);
  const activeCount = assignments.filter(a => a.status === 'active').length;

  return (
    <div className="p-6 lg:p-8 space-y-6 animate-fade-in" data-testid="school-assignments-page">
      <div>
        <h1 className="font-outfit text-2xl font-bold text-slate-900">Assignments</h1>
        <p className="text-slate-500 text-sm mt-0.5">Overview of all assignments across classes</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Total Assignments', value: total, color: 'from-[#516EFA] to-[#7B8EFC]', icon: Award },
          { label: 'Active', value: activeCount, color: 'from-emerald-500 to-emerald-400', icon: CheckCircle },
          { label: 'Submissions', value: totalSubmissions, color: 'from-amber-500 to-amber-400', icon: Users },
          { label: 'Graded', value: totalGraded, color: 'from-[#14C8C4] to-[#1BE6E2]', icon: BarChart },
        ].map((s, i) => (
          <div key={i} className="stat-card">
            <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${s.color} flex items-center justify-center mb-3`}>
              <s.icon className="w-5 h-5 text-white" />
            </div>
            <p className="font-outfit text-2xl font-bold text-slate-900">{s.value}</p>
            <p className="text-sm text-slate-500">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div className="flex gap-3">
        <Select value={filterStatus || 'all'} onValueChange={v => setFilterStatus(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-36 h-9"><SelectValue placeholder="All status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Assignments Table */}
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50">
                <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase">Assignment</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase">Teacher</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase">Classes</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase">Due Date</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase">Submissions</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase">Status</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}>
                    <td colSpan={7} className="px-5 py-3"><div className="h-8 bg-slate-100 rounded animate-pulse" /></td>
                  </tr>
                ))
              ) : assignments.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-12 text-slate-500 text-sm">No assignments found</td></tr>
              ) : assignments.map(a => (
                <tr key={a.id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                  <td className="px-5 py-4">
                    <p className="font-medium text-slate-900 text-sm">{a.title}</p>
                    <p className="text-xs text-slate-500">{a.subject_name} • {a.max_score} pts</p>
                  </td>
                  <td className="px-5 py-4 text-sm text-slate-700">{a.teacher_name}</td>
                  <td className="px-5 py-4">
                    <div className="flex gap-1 flex-wrap">
                      {(a.class_names || []).slice(0, 2).map((c, i) => <Badge key={i} className="bg-[#eef1ff] text-[#516EFA] text-xs">{c}</Badge>)}
                      {(a.class_names || []).length > 2 && <Badge className="bg-slate-100 text-slate-600 text-xs">+{(a.class_names || []).length - 2}</Badge>}
                    </div>
                  </td>
                  <td className="px-5 py-4 text-sm text-slate-700">{a.due_date || '—'}</td>
                  <td className="px-5 py-4">
                    <span className="text-sm text-slate-700">{a.submission_count || 0}</span>
                    <span className="text-xs text-slate-400 ml-1">({a.graded_count || 0} graded)</span>
                  </td>
                  <td className="px-5 py-4">
                    <Badge className={`text-xs capitalize ${a.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>{a.status}</Badge>
                  </td>
                  <td className="px-5 py-4">
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-400 hover:text-[#516EFA]" onClick={() => setViewAsgn(a)}>
                      <Eye className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* View Submissions */}
      {viewAsgn && (
        <ViewSubmissionsDialog assignment={viewAsgn} onClose={() => setViewAsgn(null)} />
      )}
    </div>
  );
}

function ViewSubmissionsDialog({ assignment, onClose }) {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/assignments/${assignment.id}/submissions`)
      .then(r => setSubmissions(r.data || []))
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, [assignment.id]);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-outfit">{assignment.title}</DialogTitle>
          <p className="text-xs text-slate-500">{assignment.subject_name} • {assignment.teacher_name} • {submissions.length} submissions</p>
        </DialogHeader>
        {loading ? (
          <div className="py-8 text-center text-slate-400 text-sm">Loading...</div>
        ) : submissions.length === 0 ? (
          <div className="py-12 text-center text-slate-400 text-sm"><Users className="w-8 h-8 mx-auto mb-2 opacity-40" />No submissions yet</div>
        ) : (
          <div className="space-y-3">
            {submissions.map(sub => (
              <div key={sub.id} className="border border-slate-100 rounded-xl p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium text-slate-900 text-sm">{sub.student_name}</p>
                    <p className="text-xs text-slate-500">{sub.class_name} • Submitted: {sub.submitted_at?.slice(0, 10)}</p>
                    <p className="text-xs text-slate-600 mt-1.5 line-clamp-2 italic">"{sub.content}"</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={`text-xs capitalize ${submissionBadge(sub.status)}`}>{sub.status}</Badge>
                    {sub.score !== null && <Badge className="bg-[#eef1ff] text-[#516EFA] font-bold text-xs">{sub.score}/{assignment.max_score}</Badge>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
